import type { components } from '@/generated/internal-api/openapi-types';
import { _isNotBlank, _notNil } from '@/littledash';
import type { Animal, AnimalApiId } from '@/model/Animal.model';

export type BenchlingWorklist = components['schemas']['BenchlingWorklistV1.schema'];
/**
 * Whether every selected animal can actually be put in a worklist.
 *
 * The server enforces the synced half too - it is the same condition, checked there against the
 * database rather than the loaded rows. Checking here as well is purely so the action can be disabled
 * with an explanation instead of failing after the user commits to it.
 *
 * `api_id` is required as well as `benchling_entity_id` because it is what the request is built from.
 * Gating on only one of the two let a selection pass while the payload silently shrank: the modal
 * promised N animals, fewer were posted, and the only clue was the count in the success toast.
 */
export const allAnimalsSynced = (animals: Array<Animal>): boolean =>
  animals.every((animal) => _notNil(animal.api_id) && _isNotBlank(animal.benchling_entity_id ?? undefined));

/** api_id is optional on the model, so narrow in one pass rather than mapping then filtering. */
export const animalApiIds = (animals: Array<Animal>): Array<AnimalApiId> =>
  animals.reduce<Array<AnimalApiId>>((ids, animal) => {
    if (_notNil(animal.api_id)) {
      // Animal types api_id as a plain string, so the prefix is asserted at this boundary.
      ids.push(animal.api_id as AnimalApiId);
    }
    return ids;
  }, []);
